import { type AccountOpeningCollectionStep } from '../../../models/AccountOpeningDescriptor'
import { itemFillable, prefillSourcesFor, type ResolvedItem } from '../domain/collections'
import { type PrefillTarget } from '../domain/prefillSources'
import { PREFILL_COPY_DEFAULTS, WIZARD_ERRORS } from '../descriptor/stepCopy'
import { usePrefill } from './usePrefill'
import { useWizardFlow } from './WizardFlowContext'

interface StepPrefill {
  label: string
  canFillAll: boolean
  isFillingAll: boolean
  runAll: () => void
  canFill: (item: ResolvedItem) => boolean
  isFilling: (item: ResolvedItem) => boolean
  runFor: (item: ResolvedItem) => void
  errorFor: (item: ResolvedItem) => string | null
  error: string | null
}

export const useStepPrefill = ({
  step,
  items,
  onFilled,
}: {
  step: AccountOpeningCollectionStep
  items: ResolvedItem[]
  onFilled: (filled: Map<string, Record<string, unknown>>) => void
}): StepPrefill => {
  const { ctx, readOnly } = useWizardFlow()

  const fill = usePrefill({
    accountId: ctx.accountId,
    prefill: step.prefill,
    errorMessage: WIZARD_ERRORS.prefill,
    onFilled,
  })

  const fillable = readOnly || !step.prefill ? [] : items.filter((item) => itemFillable(step, item))
  const isFillable = (item: ResolvedItem): boolean => fillable.some((candidate) => candidate.ref === item.ref)
  const targetOf = (item: ResolvedItem): PrefillTarget => ({ key: item.ref, sources: prefillSourcesFor(item, ctx, step) })

  return {
    label: step.prefill?.label ?? PREFILL_COPY_DEFAULTS.label,
    canFillAll: fillable.length > 0,
    isFillingAll: fill.isLoading,
    runAll: () => fill.run(fillable.map(targetOf)),
    canFill: isFillable,
    isFilling: (item) => fill.isRunning(item.ref),
    runFor: (item) => fill.run(isFillable(item) ? [targetOf(item)] : []),
    errorFor: (item) => (fill.hasFailed(item.ref) ? WIZARD_ERRORS.prefill : null),
    error: fill.error,
  }
}
